import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * Draw only the rows of the Database grid that are actually on screen.
 *
 * Caleb Pikoulas, 2026-09-14: "can we please look into the speed of the crm
 * when in 'database'? ... it takes approx 10-20 seconds to scroll once, after
 * lagging."
 *
 * The grid put every row of the agent's database into the DOM at once. A
 * database of a few thousand rows is tens of thousands of table cells, and the
 * table sizes its own columns from its content, so the browser had to
 * re-measure the whole thing on the smallest change - including the row
 * highlight that follows the mouse while scrolling. That is the lag: not the
 * data, the drawing.
 *
 * Every row still lives in React state, so sorting, the tick boxes, Select all,
 * the filters and the counts all keep working on the WHOLE database exactly as
 * before. Only what is painted changes.
 */

// Under this many rows the grid is drawn exactly as it always was - every row in
// the DOM, columns sized by the browser from the content. A couple of hundred
// rows costs nothing, and windowing a small grid only adds moving parts.
export const WINDOW_FROM = 150;

// Rows kept drawn above and below the visible band, so a flick of the wheel
// lands on rows that are already there rather than on blank space.
const OVERSCAN = 12;

// The band only moves in whole steps of this many rows. Without it every frame
// of a scroll re-draws the page for the sake of one row, which costs more than
// the drawing it saves; the overscan above covers the rows the step lags by.
const STEP = 6;

// Rows drawn on the first paint of a big grid, purely so the browser can size
// the columns from real content before those widths are frozen.
const PROBE_ROWS = 40;

// Used only until the scrolling container has been measured.
const ASSUMED_VIEWPORT = 700;

/**
 * @param rowCount    how many rows the grid holds in total
 * @param scrollRef   ref to the scrolling container
 * @param tableRef    ref to the <table> inside it
 * @param resetKey    changes when this is a different table (view, database,
 *                    tab) - throws away the frozen widths AND the row height
 * @param columnsKey  changes when the columns change (added, hidden, reordered,
 *                    renamed) - re-freezes the widths but keeps the row height,
 *                    so hiding a column does not fling the agent back to the top
 */
export default function useGridWindow({
  rowCount,
  scrollRef,
  tableRef,
  resetKey,
  columnsKey,
}) {
  const [rowH, setRowH] = useState(0);
  // { widths: number[], tableWidth: number } once the columns have been frozen.
  const [cols, setCols] = useState(null);
  // Set when the grid cannot be measured at all (no layout to read - a test
  // renderer, a hidden tab). Windowing then switches itself off and every row
  // is drawn, which is slow but never wrong. Losing rows off the bottom of an
  // agent's calling list would be a far worse bug than the one being fixed.
  const [unmeasurable, setUnmeasurable] = useState(false);
  // The first row of the band, snapped to STEP. Held as an index rather than a
  // pixel offset so an unchanged band is an unchanged state and React can skip
  // the render entirely.
  const [bandStart, setBandStart] = useState(0);
  const [viewportH, setViewportH] = useState(0);
  // Read inside the scroll listener, so a re-measure never has to tear the
  // listener down and put it back.
  const rowHRef = useRef(0);
  rowHRef.current = rowH;

  const windowed = rowCount > WINDOW_FROM && !unmeasurable;

  // A different table entirely: measure it from scratch.
  useEffect(() => {
    setRowH(0);
    setCols(null);
    setUnmeasurable(false);
    setBandStart(0);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [resetKey, scrollRef]);

  // Same rows, different columns: only the widths are stale.
  useEffect(() => {
    setCols(null);
  }, [columnsKey]);

  const measure = useCallback(() => {
    const table = tableRef.current;
    if (!table) return false;
    const heads = table.querySelectorAll("thead th");
    const rows = table.querySelectorAll("tbody tr[data-row]");
    if (!heads.length || !rows.length) return false;

    const tableWidth = Math.ceil(table.getBoundingClientRect().width);
    // The tallest of the probe rows, not the first: a row carrying a "Do not
    // contact" pill is a whisker taller, and a per-row difference would let the
    // spacers drift out of step with the real rows further down a long list.
    let tallest = 0;
    rows.forEach((row) => {
      tallest = Math.max(tallest, Math.round(row.getBoundingClientRect().height));
    });
    if (!tableWidth || !tallest) return false;

    setCols({
      widths: Array.from(heads, (th) => Math.ceil(th.getBoundingClientRect().width)),
      tableWidth,
    });
    setRowH(tallest);
    return true;
  }, [tableRef]);

  // Freeze the widths the browser chose. Runs before paint, so the agent never
  // sees the probe render.
  useLayoutEffect(() => {
    if (!windowed || cols) return;
    if (!measure()) setUnmeasurable(true);
  }, [windowed, cols, measure]);

  // Follow the scroll, one update per frame at most.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !windowed) return undefined;

    let frame = 0;
    const track = () => {
      const h = rowHRef.current;
      if (!h) return;
      const top = Math.max(0, Math.floor(el.scrollTop / h));
      const snapped = Math.floor(top / STEP) * STEP;
      // Same band as last frame - nothing to re-draw.
      setBandStart((prev) => (prev === snapped ? prev : snapped));
    };
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        track();
      });
    };
    const onResize = () => setViewportH(el.clientHeight);

    onResize();
    track();
    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(onResize) : null;
    if (ro) ro.observe(el);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      if (ro) ro.disconnect();
    };
  }, [scrollRef, windowed]);

  // The band of rows to draw. Until the table has been measured this is the
  // probe: enough rows for the browser to size the columns from.
  let first = 0;
  let last = windowed ? Math.min(rowCount, PROBE_ROWS) : rowCount;
  // Keyed on the row height alone, not on the frozen widths: hiding a column
  // drops the widths to be measured again, and the band has to stay where the
  // agent left it while that happens rather than snapping back to the top.
  const ready = Boolean(windowed && rowH);
  if (ready) {
    const band = Math.max(1, Math.ceil((viewportH || ASSUMED_VIEWPORT) / rowH));
    // bandStart lags the true top row by at most STEP rows and the overscan is
    // wider than that, so the rows on screen are always among the ones drawn.
    first = Math.max(0, Math.min(bandStart, rowCount - 1) - OVERSCAN);
    last = Math.min(rowCount, first + band + OVERSCAN * 2);
  }

  return {
    windowed,
    ready,
    first,
    last,
    rowH,
    // Spacer rows that stand in for everything not drawn, so the scrollbar and
    // the scroll position stay true to the full database.
    padTop: ready ? first * rowH : 0,
    padBottom: ready ? (rowCount - last) * rowH : 0,
    colWidths: cols?.widths || null,
    tableWidth: cols?.tableWidth || null,
  };
}
